/*
 * OIDC auth for testing
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  User,
  UserManager
} from 'oidc-client';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Constants } from '../config/constants';
import { AuthService } from 'app/shared/services/auth.service';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { Router } from '@angular/router';
import { UiConfigService } from "app/shared/services/ui-config.service";

@Injectable ( {
  providedIn: 'root'
} )
export class OidcAuthService {
  private userManager: UserManager;
  private user: User;
  private loginChangedSubject = new Subject<boolean> ();
  private authContext: any;
  private authUiConfigUrlSegmentKey: string | null = null;

  constructor (
    private httpClient: HttpClient,
    private authService: AuthService,
    configService: ConfigurationService,
    private router: Router,
    private uiConfigService: UiConfigService
  ) {
    const stsSettings = {
      authority: configService.byKey ( 'ping.federate.authority.url' ),
      client_id: configService.byKey ( 'ping.federate.client.id' ),
      redirect_uri: `${ Constants.clientRoot }authenticate`,
      scope: 'openid profile',
      response_type: 'code',
      post_logout_redirect_uri: `${ Constants.clientRoot }logout`,
      metadata: {
        issuer: configService.byKey ( 'ping.federate.metadata.issuer' ),
        authorization_endpoint: configService.byKey ( 'ping.federate.metadata.authorization.url' ),
        token_endpoint: configService.byKey ( 'ping.federate.metadata.token.url' ),
        userinfo_endpoint: configService.byKey ( 'ping.federate.metadata.user.info.url' ),
        end_session_endpoint: `${ configService.byKey ( 'ping.federate.metadata.end.session.url' ) }${ Constants.clientRoot }login/sso`,
      }
    };
    this.userManager = new UserManager ( stsSettings );
    this.userManager.events.addAccessTokenExpired ( _ => {
      this.loginChangedSubject.next ( false );
    } );
    this.userManager.events.addUserLoaded ( user => {
      if ( this.user !== user ) {
        this.user = user;
        this.loadSecurityContext ();
        this.loginChangedSubject.next ( !!user && !user.expired );
      }
    } );
  }

  login ( extraQueryParams: any, state?: any ) {
    return this.userManager.signinRedirect ( {
      extraQueryParams,
      ...( state ? { state } : {} ),
    } );
  }

  isLoggedIn (): Promise<boolean> {
    return this.userManager.getUser ().then ( user => {
      const userCurrent = !!user && !user.expired;
      if ( this.user !== user ) {
        this.loginChangedSubject.next ( userCurrent );
      }
      if ( userCurrent && !this.authContext ) {
        this.loadSecurityContext ();
      }
      this.user = user;
      return userCurrent;
    } );
  }

  completeLogin () {
    return this.userManager.signinRedirectCallback ().then ( user => {
      this.user = user;
      this.authUiConfigUrlSegmentKey = user?.state?.urlSegmentKey || null;
      this.loginChangedSubject.next ( !!user && !user.expired );
      return user;
    } );
  }

  async logout () {
    await this.userManager.signoutRedirect ();
  }

  getAccessToken () {
    return this.userManager.getUser ().then ( user => {
      if ( !!user && !user.expired ) {
        return user.access_token;
      } else {
        return null;
      }
    } );
  }

  async loadSecurityContext () {
    const token = await this.getAccessToken ();
    const queryParams = `?customAuthProviderKey=${ this.uiConfigService.urlSegmentKey }`;

    this.httpClient
      .post<any> ( `${ Constants.apiRoot }pingfederate/auth${ queryParams }`, token )
      .subscribe (
        context => {
          this.authContext = { ...context };
          const authUiConfigUrlSegmentKey = this.authUiConfigUrlSegmentKey ?? this.uiConfigService.urlSegmentKey;
          this.authUiConfigUrlSegmentKey = null;
          this.authService.oidcLogin ( this.authContext.accessToken, authUiConfigUrlSegmentKey );
        },
        error => {
          console.error ( error );
          if ( error.status === 401 ) {
            this.router.navigate ( [ this.uiConfigService.urlSegmentKey, 'login', '401' ] ).then ();
          }
        }
      );
  }

}

